import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import { tryHideFullScreenLoading } from "@/views/components/Loading/fullScreen";
import { ElMessage } from "element-plus";
import { useUserStore } from "@/stores/modules/user";
import router from "@/routers";
import type { ApiResponse } from "@/api/interface/apiResponse";
import qs from "qs";
import { hideLoading, methodWhitelist, showLoading, urlWhitelist } from "@/utils/loading";
import { GRAY_VERSION, SHIP_GRAY_VERSION } from "@zhihaoscm/mo-config";

export interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
    loading?: boolean;
    cancel?: boolean;
    returnRawResponse?: boolean;
}

// Mock数据类型
class MockResponseError extends Error {
    mockResponse: AxiosResponse;

    constructor(mockResponse: AxiosResponse) {
        super("Mock response");
        this.mockResponse = mockResponse;
    }
}

const config: AxiosRequestConfig = {
    // 默认地址请求地址，可在 .env.** 文件中修改
    baseURL: import.meta.env.DEV ? "" : import.meta.env.VITE_API_URL,
    // 设置超时时间
    timeout: 30000,

    paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: "repeat" });
    }
};

// 查船页面禁止出现全屏Loading
const checkShowLoading = (config: CustomAxiosRequestConfig) => {
    const { url, method, loading } = config as { url: string; method: string; loading?: boolean };
    return (
        urlWhitelist.some(item => new RegExp(`^${item}`).test(url)) ||
        methodWhitelist.includes(method) ||
        loading === false
    );
};

export class RequestHttp {
    service: AxiosInstance;
    public constructor(config: AxiosRequestConfig) {
        // instantiation
        this.service = axios.create(config);

        /**
         * @description 请求拦截器
         * 客户端发送请求 -> [请求拦截器] -> 服务器
         * token校验(JWT) : 接受服务器返回的 token,存储到 vuex/pinia/本地储存当中
         */
        this.service.interceptors.request.use(
            (config: CustomAxiosRequestConfig) => {
                if (!checkShowLoading(config)) {
                    showLoading();
                }
                const userStore = useUserStore();
                const whiteList = ["/file", "/login"];
                // 指派专员列表查询属于操作权限，再次过滤
                const tipGetMethodList = [
                    "/project/project-specials/selector",
                    "/shipping/customer/find/specials",
                    "/shipping/plat/find/specials",
                    "/monitor-early-warn-record/specials-selector",
                    "/shipping/plat/close"
                ];
                if (
                    (config.method !== "get" && !whiteList.some(item => config.url!.includes(item))) ||
                    tipGetMethodList.some(item => config.url!.includes(item))
                ) {
                    const permissions = userStore.userInfo.permissions;
                    if (permissions.includes("ROLE_BUTTON_TIP")) {
                        // 返回 mock 数据
                        throw new MockResponseError({
                            data: {
                                message: "This is mock data",
                                success: true,
                                data: {}
                            },
                            status: 200,
                            statusText: "OK",
                            headers: {},
                            config: config,
                            request: {}
                        });
                    }
                }

                if (config.headers && typeof config.headers.set === "function") {
                    if (userStore.token) {
                        config.headers.set("Authorization", userStore.token);
                    }
                }

                if (config.headers?.["X-Request-From"] === "shipping") {
                    config.headers.Version = SHIP_GRAY_VERSION;
                } else {
                    config.headers.Version = GRAY_VERSION;
                }

                config.headers["app-type"] = "1";

                config.headers["X-platform"] = "admin";

                return config;
            },
            (error: AxiosError) => {
                return Promise.reject(error);
            }
        );

        /**
         * @description 响应拦截器
         *  服务器换返回信息 -> [拦截统一处理] -> 客户端JS获取到信息
         */
        this.service.interceptors.response.use(
            async <T = unknown>(response: AxiosResponse<ApiResponse<T>, CustomAxiosRequestConfig>) => {
                setTimeout(() => {
                    hideLoading();
                }, 200);
                const customConfig = response.config as CustomAxiosRequestConfig;
                if (customConfig.returnRawResponse) {
                    tryHideFullScreenLoading();
                    return response as unknown as AxiosResponse<ApiResponse<T>>;
                }
                const data: ApiResponse<T> = response.data;
                tryHideFullScreenLoading();

                // 登录失效
                if (response.status === 401) {
                    router.replace({
                        name: "login"
                    });

                    return Promise.reject(data);
                }
                // 全局错误信息拦截（防止下载文件的时候返回数据流，没有 code 直接报错）
                if (data.code && data.code !== "0") {
                    ElMessage.error(data.message);
                    return Promise.reject(data);
                }
                // 成功请求（在页面上除非特殊情况，否则不用处理失败逻辑）
                return data as unknown as AxiosResponse<ApiResponse<T>>;
            },
            async (error: AxiosError | MockResponseError) => {
                if (error instanceof MockResponseError) {
                    hideLoading();
                    // 返回 mock 数据作为响应
                    return Promise.resolve(error.mockResponse);
                }
                setTimeout(() => {
                    hideLoading();
                }, 200);
                const { response } = error;
                tryHideFullScreenLoading();
                // 请求超时 && 网络错误单独判断，没有 response
                if (error.message.indexOf("timeout") !== -1) ElMessage.error("请求超时！请您稍后重试");
                if (error.message.indexOf("Network Error") !== -1) ElMessage.error("网络错误！请您稍后重试");
                // 根据服务器响应的错误状态码，做不同的处理
                if (response) {
                    if (response.config.url === "/login/wxw") {
                        await router.push("/403");
                    } else {
                        const { checkStatus } = await import("./helper/checkStatus");
                        await checkStatus(response);
                    }
                }
                // 服务器结果都没有返回(可能服务器错误可能客户端断网)，断网处理:可以跳转到断网页面
                if (!window.navigator.onLine) await router.replace("/500");
                return Promise.reject(error);
            }
        );
    }
}

export default new RequestHttp(config);

class ShippingRequestHttp extends RequestHttp {
    constructor(config: AxiosRequestConfig) {
        super(config);
    }
}

const shippingConfig = {
    ...config,
    baseURL: import.meta.env.VITE_SHIPPING_API_URL,
    headers: {
        "X-Request-From": "shipping"
    }
};
export const shippingRequestHttp = new ShippingRequestHttp(shippingConfig);
